import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { FeatherRotateCcw } from "@subframe/core";

import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { EventTypeResponseDto } from "@/generated/model/eventTypeResponseDto";
import type { NurseryItemDto } from "@/generated/model/nurseryItemDto";
import { ALL_FILTER_VALUE } from "@/lib/events-hub";

interface EventsHubFiltersProps {
  /** Only the categories actually in use by an event type — see the note below. */
  categories: CategoryResponseDto[];
  categoryId?: string;
  onCategoryChange: (categoryId: string | undefined) => void;

  eventTypes: EventTypeResponseDto[];
  eventTypeId?: string;
  onEventTypeChange: (eventTypeId: string | undefined) => void;

  /** Nurseries the user can access; the control is hidden when there is only one. */
  nurseries: NurseryItemDto[];
  selectedNurseryId: string | null;
  onNurseryChange: (nurseryId: string) => void;

  canReset: boolean;
  onReset: () => void;
}

/**
 * The Events Hub filter row: Category, Event type, Location and Reset.
 *
 * "Location" selects the nursery, which is the page's scope rather than a filter —
 * picking one writes the session-wide selection the top bar switcher also writes.
 * That is why Reset deliberately leaves it alone, matching the Task Manager.
 *
 * There is no "Uncategorised" option, unlike the Task Manager: every event type is
 * seeded with a category, so the option would match nothing.
 */
export function EventsHubFilters({
  categories,
  categoryId,
  onCategoryChange,
  eventTypes,
  eventTypeId,
  onEventTypeChange,
  nurseries,
  selectedNurseryId,
  onNurseryChange,
  canReset,
  onReset,
}: EventsHubFiltersProps) {
  const showLocation = nurseries.length > 1;

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div data-testid="events-filter-category">
        <Select
          placeholder="All categories"
          value={categoryId ?? ALL_FILTER_VALUE}
          onValueChange={(value) =>
            onCategoryChange(value === ALL_FILTER_VALUE ? undefined : value)
          }
        >
          <Select.Item value={ALL_FILTER_VALUE}>All categories</Select.Item>
          {categories.map((category) => (
            <Select.Item key={category.id} value={category.id}>
              {category.name}
            </Select.Item>
          ))}
        </Select>
      </div>

      <div data-testid="events-filter-type">
        <Select
          placeholder="All event types"
          value={eventTypeId ?? ALL_FILTER_VALUE}
          onValueChange={(value) =>
            onEventTypeChange(value === ALL_FILTER_VALUE ? undefined : value)
          }
        >
          <Select.Item value={ALL_FILTER_VALUE}>All event types</Select.Item>
          {eventTypes.map((eventType) => (
            <Select.Item key={eventType.id} value={eventType.id}>
              {eventType.name}
            </Select.Item>
          ))}
        </Select>
      </div>

      {showLocation && (
        <div data-testid="events-filter-location">
          <Select
            placeholder="All locations"
            value={selectedNurseryId ?? undefined}
            onValueChange={onNurseryChange}
          >
            {nurseries.map((nursery) => (
              <Select.Item key={nursery.id} value={nursery.id}>
                {nursery.name}
              </Select.Item>
            ))}
          </Select>
        </div>
      )}

      <Button
        variant="neutral-tertiary"
        icon={<FeatherRotateCcw />}
        disabled={!canReset}
        onClick={onReset}
      >
        Reset filters
      </Button>
    </div>
  );
}
